import { useCallback, useMemo, useState } from 'react';
import { KeyedMutator } from 'swr';
import {
  AnnotationIcon,
  BackspaceIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  ReceiptRefundIcon,
  TruckIcon,
} from '@heroicons/react/outline';
import { Notistack } from '@shared/components/Notistack/Notistack';
import { TileWrapper } from '@styles/StyledDiv';
import { Tile } from '@styles/StyledButtons';
import { CheckRequestType, IDeal, IProductsFetch } from '../../interfaces';
import { CheckRequestModal } from '../CheckRequest/CheckRequestModal';
import { FedexModal } from '../Fedex/FedexModal';
import { LENDER_CODES } from '../constants';
import { FinancingDetailsModal } from './components/FinancingDetailsModal';
import { TitlesModal } from './components/TitlesModal';
import { FinanceModal } from './components/FinanceModal';
import { LenderCommentsModal } from './components/LenderCommentsModal';

interface ITilesProps {
  deal?: IDeal;
  dealId: string;
  assetId: string;
  reloadDeal: KeyedMutator<IDeal>;
  dealData?: IProductsFetch;
}

export const Tiles = ({ dealId, deal, assetId, reloadDeal, dealData }: ITilesProps) => {
  const [checkRequestPosition, setCheckRequestPosition] = useState(0);
  const [productCancelCheckPosition, setProductCancelCheckPosition] = useState(0);
  const [titlesModalIsOpen, setTitlesModalIsOpen] = useState(0);
  const [lenderCommentsModalIsOpen, setLenderCommentsModalIsOpen] = useState(0);
  const [financingDetailsModalIsOpen, setFinancingDetailsModalIsOpen] = useState(0);
  const [fedexModalOpen, setFedexModalIsOpen] = useState(0);
  const [isFinanceModalOpen, setIsFinanceModalOpen] = useState(0);

  const isDisable = useMemo(() => {
    const disableLenders = [LENDER_CODES.GLS, LENDER_CODES.FLAG];
    const lenderCode = deal?.lenderResponse?.lenderCode ?? '';

    return disableLenders?.includes(lenderCode);
  }, [deal?.lenderResponse?.lenderCode]);
  
  const validate = useCallback(() => {
    if (dealId && dealId !== '') {
      return true;
    }
    Notistack.generalError('Please select a deal first');
    return false;
  }, [dealId]);


  return (
    <>
      <TileWrapper>
        <Tile onClick={() => validate() && setFinancingDetailsModalIsOpen(prevState => prevState + 1)}>
          <CurrencyDollarIcon />
          Financing Details
        </Tile>
        <Tile onClick={() => setFedexModalIsOpen(PrevState => PrevState + 1)}>
          <TruckIcon />
          Fedex
        </Tile>
        <Tile onClick={() => setIsFinanceModalOpen(prevState => prevState + 1)}>
          <CurrencyDollarIcon />
          Finance
        </Tile>
        <Tile onClick={() => (dealId ? setTitlesModalIsOpen(prevState => prevState + 1) : Notistack.generalError('No Deal Id Found'))}>
          <DocumentTextIcon />
          Titles
        </Tile>
        <Tile onClick={() => setCheckRequestPosition(prevState => prevState + 1)}>
          <ReceiptRefundIcon />
          Check Request
        </Tile>

        <Tile onClick={() => setProductCancelCheckPosition(prevState => prevState + 1)}>
          <BackspaceIcon />
          Product Cancel Check
        </Tile>

        <Tile disabled={!isDisable} onClick={() => setLenderCommentsModalIsOpen(prevState => prevState + 1)}>
          <AnnotationIcon />
          Lender Comments
        </Tile>
      </TileWrapper>

      {!!checkRequestPosition && (
        <CheckRequestModal
          checkRequestModalPosition={checkRequestPosition}
          dealId={dealId}
          checkRequestType={CheckRequestType.CheckRequest}
          handleClose={() => setCheckRequestPosition(0)}
          dealData={dealData}
        />
      )}

      {!!productCancelCheckPosition && (
        <CheckRequestModal
          checkRequestModalPosition={productCancelCheckPosition}
          dealId={dealId}
          checkRequestType={CheckRequestType.ProductCancelCheck}
          handleClose={() => setProductCancelCheckPosition(0)}
          dealData={dealData}
        />
      )}

      {!!titlesModalIsOpen && (
        <TitlesModal
          titleModalPosition={titlesModalIsOpen}
          dealId={dealId}
          assetId={assetId}
          onClose={() => setTitlesModalIsOpen(0)}
        />
      )}

      {!!lenderCommentsModalIsOpen && (
        <LenderCommentsModal
          lenderModalPosition={lenderCommentsModalIsOpen}
          onClose={() => setLenderCommentsModalIsOpen(0)}
        />
      )}

      {!!financingDetailsModalIsOpen && (
        <FinancingDetailsModal
          deal={deal}
          dealId={dealId}
          assetId={assetId}
          financingModalPosition = {financingDetailsModalIsOpen}
          onClose={() => setFinancingDetailsModalIsOpen(0)}
        />
      )}
      {!!fedexModalOpen && (
        <FedexModal
          handleClose={() => setFedexModalIsOpen(0)}
          fedexModalPosition={fedexModalOpen}
          reloadDeal={reloadDeal}
        />
      )}
      {!!isFinanceModalOpen && (
        <FinanceModal
          financeConditionItem={deal?.financeConditionItem}
          financeModalPostition={isFinanceModalOpen}
          assetId={assetId}
          handleClose={() => setIsFinanceModalOpen(0)}
        />
      )}
    </>
  );
};
